import { useAuth } from '@clerk/nextjs';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';

import { Routes } from 'constants/routes.constants';
import { useAfterSignInPath } from 'hooks/useAfterSignInPath';
import { StaleTime } from 'queries/types';
import { useAddFavourite } from 'restful/accounts/mutations/useAddFavourite';
import { useRemoveFavourite } from 'restful/accounts/mutations/useRemoveFavourite';
import { useGetFavourite } from 'services/api/restful/generated/accounts/accounts';
import type { FavouriteType } from 'services/api/restful/generated/nofomoRestApi.schemas';

/** Reads whether a favourite is set (cheap point read) and toggles it. */
export const useFavouriteToggle = (type: FavouriteType, value: string) => {
  const { isSignedIn, isLoaded } = useAuth();
  const { push, asPath } = useRouter();
  const { setAfterSignInUrl } = useAfterSignInPath();
  const { addFavourite } = useAddFavourite();
  const { removeFavourite } = useRemoveFavourite();

  const { data } = useGetFavourite(type, value, {
    query: {
      enabled: isLoaded && !!isSignedIn && !!value,
      staleTime: StaleTime.FIVE_MIN,
    },
  });

  // What the reader last pressed, so the control answers without waiting.
  const [pressed, setPressed] = useState<boolean>();

  const active = useMemo(
    () => pressed ?? !!data?.data?.favourite,
    [data?.data?.favourite, pressed],
  );

  const toggle = () => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      setAfterSignInUrl(asPath);
      void push(Routes.Login);
      return;
    }

    setPressed(!active);

    if (active) {
      removeFavourite(type, value, { onError: () => setPressed(true) });
    } else {
      addFavourite(type, value, { onError: () => setPressed(false) });
    }
  };

  return { active, toggle };
};
